'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, Check, ChevronRight, Ellipsis, Loader2, X } from 'lucide-react';
import { ResponsiveSheet } from '../../components/ResponsiveSheet';
import { SheetHeader } from './SheetHeader';

/**
 * The profile page's building blocks: the display cards with their "..."
 * button, the masonry they sit in, rows and switches, and the sheet every
 * editor opens in (white panel, navy gradient header). The profile widget
 * and the staff tools both build from these, so the two read the same.
 */

/* ------------------------------------------------------------------ */
/* Class strings shared with the finder family                         */
/* ------------------------------------------------------------------ */

export const CARD = 'rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm';
/** Bebas Neue, the display face the new site and the locations finder use for headings. */
export const DISPLAY = 'font-display font-normal tracking-wide uppercase';
export const CTA_DANGER =
  'inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:bg-red-700 hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100';
export const FIELD =
  'w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none';
export const LABEL = 'text-xs font-bold tracking-widest text-gray-500 uppercase';
export const CHIP =
  'inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all';
export const CHIP_IDLE =
  'bg-white text-[#1c2b39] ring-1 ring-gray-200 hover:scale-105 hover:shadow-md active:scale-95';
export const CHIP_SELECTED = 'bg-[#1c2b39] text-white shadow-md';
export const CHIP_DASHED =
  'bg-white text-[#1c2b39] ring-1 ring-gray-300 ring-dashed hover:scale-105 hover:shadow-md active:scale-95';
export const CTA =
  'inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100';
export const CTA_TEAL = `${CTA} bg-[#299c8f]`;
export const CTA_SMALL =
  'inline-flex items-center justify-center gap-1 rounded-full px-4 py-2 text-sm font-bold shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100';
export const BTN_QUIET =
  'inline-flex items-center justify-center gap-1 rounded-full bg-gray-100 px-4 py-2 text-sm font-bold text-[#1c2b39] transition-all hover:scale-105 hover:shadow-md active:scale-95';
export const LINK = 'text-sm font-semibold text-[#299c8f] hover:underline';
export const CLOSE_BUTTON =
  'absolute top-4 right-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#1c2b39] shadow-sm backdrop-blur transition-all hover:scale-105 active:scale-95 sm:top-5 sm:right-5';

/* ------------------------------------------------------------------ */
/* Page structure                                                      */
/* ------------------------------------------------------------------ */

export function Kicker({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">{children}</p>
  );
}

/** A section heading in the display face, with room for one action on the right. */
export function Heading({
  children,
  sub,
  action,
  size = 'text-3xl sm:text-4xl',
}: {
  children: ReactNode;
  /** A quiet line under the heading */
  sub?: ReactNode;
  action?: ReactNode;
  size?: string;
}) {
  return (
    <div className="flex items-end justify-between gap-4 px-1">
      <div className="min-w-0">
        <h2 className={`${DISPLAY} ${size} leading-none text-[#1c2b39]`}>{children}</h2>
        {sub && <p className="mt-1 text-sm text-gray-500">{sub}</p>}
      </div>
      {action && <div className="shrink-0 pb-0.5 whitespace-nowrap">{action}</div>}
    </div>
  );
}

/** One block of the scrolling page: a display heading, an optional action beside it, then its content. */
export function Section({
  kicker,
  sub,
  action,
  children,
  className = '',
}: {
  kicker: string;
  sub?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`flex flex-col gap-4 ${className}`}>
      <Heading sub={sub} action={action}>
        {kicker}
      </Heading>
      {children}
    </section>
  );
}

/** The small teal text action that sits beside a heading ("Find a group", "Add someone"). */
export function HeadAction({
  onClick,
  href,
  children,
}: {
  onClick?: () => void;
  href?: string;
  children: ReactNode;
}) {
  const cls =
    'inline-flex items-center gap-1 text-sm font-bold text-[#299c8f] transition-colors hover:text-[#1c2b39] hover:underline';
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
        <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
      <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );
}

/** The one control a display card carries: a quiet "..." disc that opens the card's sheet. */
export function EditButton({
  onClick,
  label = 'Open',
  className = '',
}: {
  onClick: () => void;
  /** Read by screen readers and shown on hover; the disc itself only shows "..." */
  label?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[#1c2b39] transition-all hover:scale-105 hover:bg-gray-200 hover:shadow-sm active:scale-95 ${className}`}
    >
      <Ellipsis className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );
}

/**
 * A card that shows facts and carries one Edit button; the sheet does the
 * editing. Nothing on the card is an input.
 */
export function FactCard({
  title,
  icon,
  onEdit,
  editLabel,
  children,
  className = '',
  tone = 'white',
  header,
  actionOnHeader = false,
}: {
  title: ReactNode;
  icon?: ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  children: ReactNode;
  className?: string;
  /** `navy` for the card that should draw the eye first */
  tone?: 'white' | 'navy' | 'soft';
  /** Edge to edge above the title: a map, a photo. Sized by the caller. */
  header?: ReactNode;
  /** Put the "..." over the header's top-right corner instead of beside the title. */
  actionOnHeader?: boolean;
}) {
  const surface =
    tone === 'navy'
      ? 'bg-[#1c2b39] text-white ring-0'
      : tone === 'soft'
        ? 'bg-gray-50 ring-1 ring-gray-200/70'
        : CARD;
  // The whole card is the tap target, not only the Edit pill (Colton: "any of
  // these cards should be clickable anywhere"). Controls inside a card that do
  // something else stop the click from reaching here.
  const open = onEdit
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onEdit,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit();
          }
        },
      }
    : {};
  const body = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span
          className={`flex min-w-0 items-center gap-2 ${DISPLAY} text-xl leading-none ${tone === 'navy' ? 'text-white' : 'text-[#1c2b39]'}`}
        >
          {icon && (
            <span className={`shrink-0 ${tone === 'navy' ? 'text-[#4cb8ab]' : 'text-[#299c8f]'}`}>
              {icon}
            </span>
          )}
          <span className="truncate">{title}</span>
        </span>
        {onEdit && !(header && actionOnHeader) && (
          <span onClick={(e) => e.stopPropagation()}>
            <EditButton
              onClick={onEdit}
              label={editLabel}
              className={tone === 'navy' ? 'bg-white/15 text-white hover:bg-white/25' : ''}
            />
          </span>
        )}
      </div>
      {children}
    </>
  );
  const interactive = onEdit
    ? 'cursor-pointer transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.995]'
    : '';
  if (header) {
    // The header runs to the card's edges, so the padding moves onto the body.
    return (
      <div
        {...open}
        className={`flex flex-col overflow-clip rounded-3xl text-left ${surface} ${interactive} ${className}`}
      >
        <div className="relative">
          {header}
          {onEdit && actionOnHeader && (
            <span className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
              <EditButton
                onClick={onEdit}
                label={editLabel}
                className="bg-white/90 shadow-sm backdrop-blur hover:bg-white"
              />
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-3 p-5">{body}</div>
      </div>
    );
  }
  return (
    <div
      {...open}
      className={`flex flex-col gap-3 rounded-3xl p-5 text-left ${surface} ${interactive} ${className}`}
    >
      {body}
    </div>
  );
}

/**
 * A mosaic of cards: equal column widths (1 / 2 / 3 by the widget's own
 * width), every card its natural height, each one slotting in under the
 * shortest column so far. Plain CSS grid does it with 1px rows: each item
 * measures its card and spans that many rows (plus the gap it carries as
 * bottom padding), and auto-placement puts the next item in the first free
 * cell, which is the top of the shortest column. DOM order never changes,
 * so cards keep their state when the layout shifts.
 */
export function Masonry({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`grid grid-cols-1 gap-x-4 @xl:grid-cols-2 @4xl:grid-cols-3 ${className}`}
      style={{ gridAutoRows: '1px' }}
    >
      {children}
    </div>
  );
}

/** One cell of a Masonry: measures the card inside and spans that many 1px rows. `pb-4` is the row gap. */
export function MasonryItem({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [span, setSpan] = useState<number | null>(null);
  useLayoutEffect(() => {
    const cell = ref.current;
    if (!cell) return;
    const measure = () => {
      const card = cell.firstElementChild as HTMLElement | null;
      const h = card ? card.getBoundingClientRect().height : 0;
      setSpan(h > 0 ? Math.ceil(h) + GAP : 0);
    };
    measure();
    const card = cell.firstElementChild;
    if (!card || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    return () => ro.disconnect();
  }, [children]);
  if (span === 0) return <div ref={ref} className="hidden" />;
  return (
    <div
      ref={ref}
      className={`min-w-0 pb-4 ${className}`}
      style={span ? { gridRowEnd: `span ${span}` } : undefined}
    >
      {children}
    </div>
  );
}
const GAP = 16;

/**
 * Marks a personal fact (a name, a face, contact details, an address, a
 * birthday, money) so the page can blur it for a demo or a recording:
 * `?blur=1` on the page puts `blur-pii` on the widget's hosts and the
 * stylesheet blurs every `.pii` under it. Never affects the data.
 */
export function Pii({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`pii ${className}`}>{children}</span>;
}

/** A "label / value" pair inside a FactCard. Missing values read as an invitation, not an error. */
export function Fact({
  label,
  value,
  missing = 'Add',
  className = '',
}: {
  label: string;
  value: ReactNode | null | undefined;
  missing?: string;
  className?: string;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={`flex min-w-0 flex-col gap-0.5 ${className}`}>
      <span className="text-[11px] font-bold tracking-widest text-gray-400 uppercase">{label}</span>
      <span
        className={`min-w-0 text-sm ${empty ? 'font-medium text-gray-400' : 'font-semibold text-current'}`}
      >
        {empty ? missing : value}
      </span>
    </div>
  );
}

/** Facts in two columns when the card is wide enough, one when it is not. */
export function FactGrid({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`grid grid-cols-1 gap-x-4 gap-y-3 @xs:grid-cols-2 ${className}`}>
      {children}
    </div>
  );
}

export function Chip({
  active,
  onClick,
  children,
  dashed = false,
  disabled = false,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  dashed?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`${CHIP} ${active ? CHIP_SELECTED : dashed ? CHIP_DASHED : CHIP_IDLE} disabled:opacity-60`}
    >
      {children}
    </button>
  );
}

/** A tappable "label: value" row inside a card, opening a form. */
export function Row({
  label,
  value,
  onClick,
  muted = false,
  leading,
  below,
}: {
  label: string;
  value: ReactNode;
  onClick?: () => void;
  /** The value is a placeholder ("Add") rather than a fact */
  muted?: boolean;
  leading?: ReactNode;
  /** A quiet line under the row (its own control, so it may hold a button) */
  below?: ReactNode;
}) {
  const inner = (
    <>
      {leading}
      <span className="min-w-0 flex-1 text-sm text-gray-500">{label}</span>
      <span
        className={`max-w-[60%] min-w-0 truncate text-right text-sm ${muted ? 'font-medium text-gray-400' : 'font-semibold text-[#1c2b39]'}`}
      >
        {value}
      </span>
      {onClick && <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2.5} />}
    </>
  );
  const base = 'flex w-full items-center gap-3 px-4 py-3.5 text-left';
  const row = onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${base} transition-colors hover:bg-gray-50 active:bg-gray-100`}
    >
      {inner}
    </button>
  ) : (
    <div className={base}>{inner}</div>
  );
  if (!below) return row;
  return (
    <div>
      {row}
      <div className="-mt-2 px-4 pb-3">{below}</div>
    </div>
  );
}

/** Rows stacked inside one card with hairlines between them. */
export function RowCard({ children }: { children: ReactNode }) {
  return <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>{children}</div>;
}

/** A switch row: whole row is the control, the switch flips at once and stays put while saving. */
export function SwitchRow({
  title,
  description,
  on,
  saving = false,
  onToggle,
  leading,
}: {
  title: string;
  description?: ReactNode;
  on: boolean;
  saving?: boolean;
  onToggle: (next: boolean) => void;
  leading?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={title}
      onClick={() => onToggle(!on)}
      className="flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      {leading}
      <span className={`min-w-0 flex-1 transition-opacity ${saving ? 'opacity-50' : ''}`}>
        <span className="flex items-center gap-1.5 text-sm font-semibold text-[#1c2b39]">
          {title}
          {saving && <Loader2 aria-hidden className="h-3 w-3 shrink-0 animate-spin" />}
        </span>
        {description && (
          <span className="mt-0.5 block text-xs leading-snug text-gray-500">{description}</span>
        )}
      </span>
      <span
        aria-hidden
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-[#299c8f]' : 'bg-gray-300'}`}
      >
        <span
          className={`block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
        />
      </span>
    </button>
  );
}

export function Bar({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-full bg-gray-200 ${className}`} />;
}

export function RowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`} aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-4">
          <Bar className="h-3 w-24" />
          <span className="flex-1" />
          <Bar className="h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

export function CardsSkeleton({ count = 2 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`${CARD} flex items-center gap-3 p-4`}>
          <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />
          <div className="flex flex-1 flex-col gap-2">
            <Bar className="h-3.5 w-2/3" />
            <Bar className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center">
      {children}
    </div>
  );
}

export function InlineError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1.5 text-sm font-semibold text-red-600" role="alert">
      <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2.5} />
      {message}
    </p>
  );
}

/** Save button state text inside forms: "Saved" flashes, then goes away. */
export function SavedMark({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#299c8f]">
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
      Saved
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* The sheet every editor opens in: white panel, navy gradient header  */
/* ------------------------------------------------------------------ */

export function ProfileSheet({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  defaultPage = 'main',
  maxWidth = 'max-w-2xl',
  header,
  footer,
  placement = 'center',
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  defaultPage?: string;
  maxWidth?: string;
  /** Replaces the default gradient header */
  header?: ReactNode;
  /** Pinned under the scroll area on every page; gate it on `useResponsiveSheet().currentPage` inside */
  footer?: ReactNode;
  /** `right`: a full-height drawer on desktop (the login widget); phones keep the bottom sheet */
  placement?: 'center' | 'right';
}) {
  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      defaultPage={defaultPage}
      maxWidth={maxWidth}
      placement={placement}
      footer={footer}
      noPanelPadding
      panelClassName="bg-white"
      // The site's radius: the phone sheet already rounds its top 2rem, the
      // desktop panel matches it on all four corners.
      className="@container rounded-[2rem]"
      hideCloseButton
      header={
        <div className="relative">
          {header ?? <SheetHeader title={title} subtitle={subtitle} icon={icon} />}
          <button type="button" aria-label="Close" onClick={onClose} className={CLOSE_BUTTON}>
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      }
    >
      {children}
    </ResponsiveSheet>
  );
}

/**
 * Wraps a SheetFormPage so its submit button wears the finder family's
 * teal pill. The package paints `bg-primary` (navy, square); Tailwind
 * resolves by stylesheet order, so the override needs `!`.
 */
export function FormLook({ children }: { children: ReactNode }) {
  return (
    <div className="[&_button[type=submit]]:rounded-full [&_button[type=submit]]:bg-[#299c8f]! [&_button[type=submit]]:px-6 [&_button[type=submit]]:shadow-sm [&_button[type=submit]]:transition-all [&_button[type=submit]]:hover:scale-[1.02] [&_button[type=submit]]:hover:shadow-md [&_button[type=submit]]:active:scale-[0.98] [&_kbd]:hidden">
      {children}
    </div>
  );
}

/** Body padding for a sheet page that is not a form. */
export function PageBody({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`flex flex-col gap-5 px-5 py-5 sm:px-6 ${className}`}>{children}</div>;
}
